import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  withStyles,
} from '@material-ui/core';

import BaseDialog from './baseDialog';

import {
  Field,
  getFormValues,
  reduxForm,
} from 'redux-form';

import {renderCheckbox, renderSelect, renderTextField} from '../../../../atoms/CustomPart';
import {createSearchList} from '../../../../../common/ComboParts';
import * as validateRule from '../../../../validateRule';
import {ON_SITE_REBUILD_UMU} from '../data';
import OnSiteRebuildTooltip from '../atoms/onSiteRebuildTooltip';

const styles = (theme) => ({
  // fields: {
  //   margin: '0px 10px 15px 10px',
  // },
  guide: {
    marginBottom: theme.spacing(1),
  },
  multiSelectedGuide: {
    marginBottom: theme.spacing(1),
    color: theme.palette.error.main,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  rowField: {
    flex: '1 1 auto',
    minWidth: 0,
  },
});

/**
 * 一括変更の対象にできる項目。
 * 複数電柱選択時は、項目ごとのチェックボックス(フォーム値`checked.<項目名>`)で
 * 変更対象を選択し、チェックされた項目のみ活性化・必須チェック・一覧への反映を行う。
 * 1件選択時はチェックボックスを表示せず、全項目チェック済みとして扱う
 */
const BULK_EDIT_FIELDS = [
  'DrSideKoziNecessity__c',
  'KoziGroup__c',
  'DrSideKoziCostNecessity__c',
  'OnSiteRebuildUmu__c',
  'Remarks__c',
];

/**
 * 変更対象チェックボックスのフォーム値の名前。
 * @param {string} name 項目名
 * @return {string} チェックボックスのフォーム値の名前
 */
const checkedName = (name) => `checked.${name}`;

/**
 * 項目が変更対象としてチェックされているか。
 * @param {object} allValues フォーム全体の値
 * @param {string} name 項目名
 * @return {boolean} チェックされている場合true
 */
const isChecked = (allValues, name) => !!allValues?.checked?.[name];

/**
 * 変更対象としてチェックされている場合のみ、指定のバリデーションを行う。
 * @param {string} name 項目名
 * @param {function} rule バリデーション関数
 * @return {function} バリデーション関数
 */
const validateIfChecked = (name, rule) => (value, allValues) =>
  isChecked(allValues, name) ? rule(value) : undefined;

/**
 * 備考が必須かどうか。
 * 改造工事費用要否・備考の両方が変更対象で、改造工事費用要否が「不要」の場合のみ必須
 * (一方のみ変更対象の場合の整合性は、一覧の確定時チェックで確認する)
 * @param {object} allValues フォーム全体の値
 * @return {boolean} 必須の場合true
 */
const isRemarksRequired = (allValues) =>
  isChecked(allValues, 'DrSideKoziCostNecessity__c') &&
  isChecked(allValues, 'Remarks__c') &&
  allValues?.DrSideKoziCostNecessity__c === '0';

/**
 * 備考の必須バリデーショ。
 * @param {string} value 備考の入力値
 * @param {object} allValues フォーム全体の値
 * @return {string|undefined} エラーメッセージ(問題なければundefined)
 */
const validateRemarksRequired = (value, allValues) =>
  isRemarksRequired(allValues) ? validateRule.required(value) : undefined;

// Fieldのvalidate propは参照が変わるたびにredux-formが再登録・再検証を行うため、
// render毎に新しい配列/関数が生成されないよう、モジュールスコープで固定する。
// (JSX内にインラインで書くと、値に応じて結果が変わるvalidateRemarksRequiredと
// 組み合わさった際にMaximum update depth exceededの無限ループを引き起こす)
const remarksValidate = [
  validateRemarksRequired,
  validateRule.maxLength(30),
];
const koziNecessityValidate = [
  validateIfChecked('DrSideKoziNecessity__c', validateRule.required),
];
const koziGroupValidate = [
  validateIfChecked('KoziGroup__c', validateRule.required4),
];
const koziCostNecessityValidate = [
  validateIfChecked('DrSideKoziCostNecessity__c', validateRule.required),
];
const onSiteRebuildValidate = [
  validateIfChecked('OnSiteRebuildUmu__c', validateRule.required),
];

/** 全項目チェック済みの状態(1件選択時に使用) */
const ALL_CHECKED = Object.fromEntries(
    BULK_EDIT_FIELDS.map((name) => [name, true]));

/**
 * 一括変更モードかどうか。
 * 複数電柱選択時は、変更対象の項目をチェックボックスで選択する一括変更モードにする。
 * 参照専用時は変更しないため、チェックボックスを表示せず先頭の電柱の値を表示する
 * @param {Array} selectedDntList 選択中の電柱リスト
 * @param {boolean} readOnly 参照専用の場合true
 * @return {boolean} 一括変更モードの場合true
 */
const isBulkEditMode = (selectedDntList, readOnly) =>
  selectedDntList.length > 1 && !readOnly;

const mapStateToProps = (state) => ({
});

const mapDispatchToProps = {
};

/**
 * 変更対象チェックボックス付きの入力行。
 * @param {object} props
 * @param {object} props.classes スタイル
 * @param {string} props.name 項目名
 * @param {boolean} props.isBulkEdit 一括変更モードの場合true(チェックボックスを表示する)
 * @param {boolean} props.checkDisabled チェックボックスを非活性にする場合true
 * @param {function} props.onCheckChange チェックボックス変更時処理
 * @param {node} props.tooltip 項目名の横に表示するツールチップ
 * @param {node} props.children 入力項目
 * @return {JSX.Element}
 */
const BulkEditRow = ({
  classes, name, isBulkEdit, checkDisabled, onCheckChange, tooltip, children,
}) => (
  <Grid item xs={12}>
    <Box className={classes.row}>
      {isBulkEdit && (
        <Field
          name={checkedName(name)}
          component={renderCheckbox}
          disabled={checkDisabled}
          onChange={onCheckChange}
        />
      )}
      <Box className={classes.rowField}>
        {children}
      </Box>
      {tooltip}
    </Box>
  </Grid>
);

BulkEditRow.propTypes = {
  classes: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  isBulkEdit: PropTypes.bool.isRequired,
  checkDisabled: PropTypes.bool,
  onCheckChange: PropTypes.func.isRequired,
  tooltip: PropTypes.node,
  children: PropTypes.node,
};

const DntEditDialog = (props) => {
  const {
    classes,
    selectedDntList,
    disabled,
    readOnly,
    comboItems,
    values,
    onOk,
    handleSubmit,
    open,
    initialize,
    initialValues,
    change,
    onCancel,
  } = props;

  const isMultiSelected = selectedDntList.length > 1;

  const isBulkEdit = isBulkEditMode(selectedDntList, readOnly);

  // ダイアログを開くたびに、確実にフォームをinitialValuesで再初期化
  // (一括変更モードに応じた初期値はDntEditDialogContainerで設定済み)
  React.useEffect(() => {
    if (open) {
      initialize(initialValues);
    }
  }, [open, initialize, initialValues]);

  // 改造工事費用要否が「不要」の場合、備考を必須にする
  const remarksRequired = isRemarksRequired(values);

  // 選択項目に「有(元戻し)」の電柱を含む場合、確定時に元位置建替有無の値を
  // 設定しない(onDecideDntEdit参照)ため、この項目自体も非活性にする
  const hasRestoreSelected = selectedDntList.some((dnt) =>
    dnt.dataItem.OnSiteRebuildUmu__c === ON_SITE_REBUILD_UMU.RESTORE);
  // 参照専用時も同様に非活性にする
  const onSiteRebuildDisabled = readOnly || hasRestoreSelected;

  // 入力項目の非活性判定(一括変更モードでは、チェックされていない項目も非活性にする)。
  // 必須表示(required)も同様に、チェックされている項目のみ表示する
  // (1件選択時・参照専用時は全項目チェック済みのため従来どおり表示される)
  const isFieldDisabled = (name, baseDisabled) =>
    baseDisabled || (isBulkEdit && !isChecked(values, name));

  // 全項目選択チェックボックスの対象(非活性の元位置建替有無は対象外)
  const checkTargets = BULK_EDIT_FIELDS.filter((name) =>
    !(name === 'OnSiteRebuildUmu__c' && onSiteRebuildDisabled));
  const checkedCount =
    checkTargets.filter((name) => isChecked(values, name)).length;
  const isAllChecked =
    checkedCount > 0 && checkedCount === checkTargets.length;
  const isPartChecked =
    checkedCount > 0 && checkedCount < checkTargets.length;

  // チェックを外した項目は値もクリアする(値が残ると反映されるように見えてしまうため)
  const onCheckChange = React.useCallback((_e, checked, _prev, fieldName) => {
    if (!checked) {
      change(fieldName.replace(/^checked\./, ''), '');
    }
  }, [change]);

  const onAllCheckChange = (e) => {
    const checked = e.target.checked;
    for (const name of checkTargets) {
      change(checkedName(name), checked);
      if (!checked) {
        change(name, '');
      }
    }
  };

  const combos = React.useMemo(() => {
    // 「有(元戻し)」は「有(仮移設)」から自動追加/削除される項目のため、
    // 選択項目に「有(元戻し)」を含まない場合は選択肢自体から除外し、手動設定できないようにする
    const onSiteRebuildItems = onSiteRebuildDisabled ?
      comboItems.onSiteRebuild :
      comboItems.onSiteRebuild.filter(
          (item) => item.code !== ON_SITE_REBUILD_UMU.RESTORE);

    return {
      necessity: createSearchList(comboItems.necessity, 'code', 'value', null, true),
      group: createSearchList(comboItems.group, 'code', 'value', null, true),
      onSiteRebuild: createSearchList(onSiteRebuildItems, 'code', 'value', null, true),
    };
  }, [comboItems, onSiteRebuildDisabled]);

  const rowProps = {classes, isBulkEdit, onCheckChange};

  return (
    <BaseDialog
      selectedDntList={selectedDntList}
      // 一括変更モードで変更対象が1つもチェックされていない場合は確定不可
      disabled={disabled || (isBulkEdit && checkedCount === 0)}
      readOnly={readOnly}
      open={open}
      onOk={handleSubmit(onOk)}
      onCancel={onCancel}
    >
      {isBulkEdit && (
        <Box className={classes.guide}>
          <Box>値を変更する項目を選択してください</Box>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={isAllChecked}
                indeterminate={isPartChecked}
                onChange={onAllCheckChange}
              />
            }
            label="全項目選択"
          />
        </Box>
      )}

      {isMultiSelected && readOnly && (
        <Box className={classes.multiSelectedGuide}>
          先頭の電柱の情報を表示しています
        </Box>
      )}

      <Grid
        key="search-1"
        container
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={2}
        style={{marginBottom: '10px'}}
      >
        <BulkEditRow {...rowProps} name="DrSideKoziNecessity__c">
          <Field
            name="DrSideKoziNecessity__c"
            className={classes.fields}
            label="工事要否"
            component={renderSelect}
            fullWidth
            type="text"
            required={isChecked(values, 'DrSideKoziNecessity__c')}
            disabled={isFieldDisabled('DrSideKoziNecessity__c', readOnly)}
            validate={koziNecessityValidate}
          >
            {combos.necessity}
          </Field>
        </BulkEditRow>

        <BulkEditRow {...rowProps} name="KoziGroup__c">
          <Field
            name="KoziGroup__c"
            className={classes.fields}
            label="工事グループ"
            component={renderSelect}
            fullWidth
            type="text"
            required={isChecked(values, 'KoziGroup__c')}
            disabled={isFieldDisabled('KoziGroup__c', readOnly)}
            validate={koziGroupValidate}
          >
            {combos.group}
          </Field>
        </BulkEditRow>

        <BulkEditRow {...rowProps} name="DrSideKoziCostNecessity__c">
          <Field
            name="DrSideKoziCostNecessity__c"
            className={classes.fields}
            label="改造工事費用要否"
            component={renderSelect}
            fullWidth
            type="text"
            required={isChecked(values, 'DrSideKoziCostNecessity__c')}
            disabled={isFieldDisabled('DrSideKoziCostNecessity__c', readOnly)}
            validate={koziCostNecessityValidate}
          >
            {combos.necessity}
          </Field>
        </BulkEditRow>

        <BulkEditRow
          {...rowProps}
          name="OnSiteRebuildUmu__c"
          checkDisabled={onSiteRebuildDisabled}
          tooltip={<OnSiteRebuildTooltip disabled={hasRestoreSelected} />}
        >
          <Field
            name="OnSiteRebuildUmu__c"
            className={classes.fields}
            label="元位置建替有無"
            component={renderSelect}
            fullWidth
            type="text"
            required={isChecked(values, 'OnSiteRebuildUmu__c')}
            disabled={isFieldDisabled('OnSiteRebuildUmu__c', onSiteRebuildDisabled)}
            validate={onSiteRebuildValidate}
          >
            {combos.onSiteRebuild}
          </Field>
        </BulkEditRow>

        <BulkEditRow {...rowProps} name="Remarks__c">
          <Field
            name="Remarks__c"
            className={classes.fields}
            label="備考"
            component={renderTextField}
            fullWidth
            type="text"
            multiline
            rows={2}
            required={remarksRequired}
            disabled={isFieldDisabled('Remarks__c', readOnly)}
            inputProps={{
              maxLength: 30,
            }}
            validate={remarksValidate}
          />
        </BulkEditRow>
      </Grid>
    </BaseDialog>
  );
};

DntEditDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  selectedDntList: PropTypes.array.isRequired,
  disabled: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
  comboItems: PropTypes.object.isRequired,
  values: PropTypes.object,
  onOk: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool,
  initialize: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  change: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const FORM_NAME = 'DntEditDialog';

const FormedTemplate = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: true,
  enableReinitialize: true,
})(connect((state) => ({
  values: getFormValues(FORM_NAME)(state),
}))(DntEditDialog));

/**
 * 一括変更モードに応じたフォームの初期値を、reduxFormのinitialValuesとして渡す。
 * enableReinitializeが有効なため、initialize()で別の値を設定してもpropsの
 * initialValuesに戻されてしまう。そのため、変更対象チェックボックス(checked)を含む
 * 初期値はreduxFormに渡す前に組み立てる
 * ・一括変更モード: 全項目を空・未チェック
 * ・それ以外: 呼び出し元のinitialValues(先頭の電柱の値)を全項目チェック済み
 * @param {object} props
 * @return {JSX.Element}
 */
const DntEditDialogContainer = (props) => {
  const {selectedDntList, readOnly, initialValues} = props;
  const isBulkEdit = isBulkEditMode(selectedDntList, readOnly);

  const formInitialValues = React.useMemo(() => (isBulkEdit ?
    {checked: {}} :
    {...initialValues, checked: ALL_CHECKED}
  ), [isBulkEdit, initialValues]);

  return <FormedTemplate {...props} initialValues={formInitialValues} />;
};

DntEditDialogContainer.propTypes = {
  selectedDntList: PropTypes.array.isRequired,
  readOnly: PropTypes.bool,
  initialValues: PropTypes.object,
};

export default withStyles(styles)(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    )(DntEditDialogContainer),
);
